import { parseApiError } from '@/lib/apiError'
import { supabase } from '@/lib/supabase'
import type { PaidPlanId } from '../../shared/billing/plans'

export type BillingAccessMode =
  | 'legacy_open'
  | 'introductory'
  | 'subscription'
  | 'complimentary'
  | 'grace'
  | 'payment_required'

export interface BillingStatus {
  accessMode: BillingAccessMode
  effectivePlan: 'free' | PaidPlanId
  enforcementEnabled: boolean
  reportsUsed: number
  reportLimit: number | null
  reportsRemaining: number | null
  subscriptionStatus: string | null
  currentPeriodEnd: string | null
  cancelAtPeriodEnd?: boolean
}

type BillingDestination = 'checkout' | 'portal'

interface BillingRedirectResponse {
  url: string
}

function esEntornoDesarrollo(): boolean {
  return import.meta.env.DEV
}

export function validarUrlRedireccionFacturacion(value: unknown, destination: BillingDestination): string {
  try {
    if (typeof value !== 'string') throw new Error()
    const url = new URL(value)
    const stripeHost = destination === 'checkout' ? 'checkout.stripe.com' : 'billing.stripe.com'
    const esStripeSeguro = url.protocol === 'https:'
      && url.hostname === stripeHost
      && url.port === ''
      && !url.username
      && !url.password
    const esLoopbackDesarrollo = esEntornoDesarrollo()
      && url.protocol === 'http:'
      && url.hostname === 'localhost'
      && !url.username
      && !url.password

    if (!esStripeSeguro && !esLoopbackDesarrollo) throw new Error()
    return url.href
  } catch {
    throw new Error('URL de facturación no segura.')
  }
}

async function headersAuth(): Promise<Record<string, string>> {
  const { data } = await supabase.auth.getSession()
  if (!data.session?.access_token) throw new Error('Debes iniciar sesión para administrar tu facturación.')
  return { Authorization: `Bearer ${data.session.access_token}` }
}

async function obtenerRespuestaFacturacion<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(path, {
    ...init,
    headers: {
      ...(init?.headers ?? {}),
      ...await headersAuth(),
    },
  })
  if (!response.ok) throw await parseApiError(response, 'No fue posible actualizar la facturación.')
  return response.json() as Promise<T>
}

function obtenerUrlRedireccion(response: BillingRedirectResponse, destination: BillingDestination): string {
  return validarUrlRedireccionFacturacion(response.url, destination)
}

function navegar(url: string): void {
  globalThis.location.assign(url)
}

export async function obtenerEstadoFacturacion(): Promise<BillingStatus> {
  return obtenerRespuestaFacturacion<BillingStatus>('/api/billing/status')
}

export async function crearCheckout(plan: PaidPlanId): Promise<void> {
  const response = await obtenerRespuestaFacturacion<BillingRedirectResponse>('/api/billing/create-checkout-session', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ plan }),
  })
  navegar(obtenerUrlRedireccion(response, 'checkout'))
}

export async function crearPortalFacturacion(): Promise<void> {
  const response = await obtenerRespuestaFacturacion<BillingRedirectResponse>('/api/billing/create-portal-session', {
    method: 'POST',
  })
  navegar(obtenerUrlRedireccion(response, 'portal'))
}
